// Replaces Turbo's native browser confirm() with a custom, on-brand dialog — app-wide.
// Hooks Turbo's confirm extension point, so every element using `data-turbo-confirm`
// (links and button_to forms) gets the styled dialog instead of window.confirm.
// Destructive actions (DELETE, or anything marked `data-confirm-danger`, e.g. an
// irreversible POST) get a red confirm button; everything else uses the primary (green) button.

const ICONS = {
  danger:
    '<svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" /></svg>',
  primary:
    '<svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>',
}

export function isDangerous(element) {
  if (!element) return false
  if (element.dataset && element.dataset.confirmDanger === "true") return true
  if (element.dataset && (element.dataset.turboMethod || "").toLowerCase() === "delete") return true
  const methodField = element.querySelector && element.querySelector('input[name="_method"]')
  return !!(methodField && (methodField.value || "").toLowerCase() === "delete")
}

function buildDialog(message, danger) {
  const overlay = document.createElement("div")
  overlay.className = "fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4"
  overlay.setAttribute("role", "dialog")
  overlay.setAttribute("aria-modal", "true")

  const confirmClasses = danger ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"

  overlay.innerHTML = `
    <div data-backdrop class="fixed inset-0 bg-gray-500/75 transition-opacity"></div>
    <div class="relative w-full max-w-md transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all">
      <div class="px-5 pt-5 pb-4 sm:p-6">
        <div class="flex gap-4">
          <div class="shrink-0 mt-0.5">${danger ? ICONS.danger : ICONS.primary}</div>
          <div class="min-w-0">
            <h3 class="text-base font-semibold text-gray-900">Bestätigung</h3>
            <p class="mt-1 text-sm text-gray-600" style="white-space: pre-line;"></p>
          </div>
        </div>
        <div class="mt-5 flex justify-end gap-3">
          <button type="button" data-cancel class="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-xs hover:bg-gray-50">Abbrechen</button>
          <button type="button" data-confirm class="inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-xs ${confirmClasses}">Bestätigen</button>
        </div>
      </div>
    </div>
  `
  overlay.querySelector("p").textContent = message
  return overlay
}

// Reusable custom confirm dialog. Returns a Promise<boolean>. Import this from JS
// controllers that previously called the native window.confirm() and `await` it.
export function confirmDialog(message, { danger = false } = {}) {
  return new Promise((resolve) => {
    const overlay = buildDialog(message, danger)
    const confirmButton = overlay.querySelector("[data-confirm]")

    const finish = (result) => {
      document.removeEventListener("keydown", onKeydown, true)
      overlay.remove()
      resolve(result)
    }
    const onKeydown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault()
        finish(false)
      } else if (event.key === "Enter") {
        event.preventDefault()
        finish(true)
      }
    }

    overlay.querySelector("[data-backdrop]").addEventListener("click", () => finish(false))
    overlay.querySelector("[data-cancel]").addEventListener("click", () => finish(false))
    confirmButton.addEventListener("click", () => finish(true))
    document.addEventListener("keydown", onKeydown, true)

    document.body.appendChild(overlay)
    confirmButton.focus()
  })
}

// Turbo's confirm hook derives the danger style from the triggering element — the form, or
// for a button_to the button that submits it.
function customConfirm(message, element, submitter) {
  return confirmDialog(message, { danger: isDangerous(element) || isDangerous(submitter) })
}

if (typeof Turbo !== "undefined") {
  // Prefer the current API; fall back to the (deprecated) setConfirmMethod on older Turbo.
  if (Turbo.config && Turbo.config.forms) {
    Turbo.config.forms.confirm = customConfirm
  } else if (typeof Turbo.setConfirmMethod === "function") {
    Turbo.setConfirmMethod(customConfirm)
  }
}
